import { uuid } from "@/interfaces";
import { PropertyUserMetadata } from "@/models/properties.model.ts";
import { store } from "@/lib/store.ts";
import { requirePermission } from "@/lib/permissions";
import { api } from "@/lib/api";
import { toAssignmentDetails, toPropertyUser, type PropertyUserRecord } from "@/repositories/property-people-records";

export type AssignableUser = { id: uuid; name: string; email: string };

const actingOrganization = () => store.getState().organization.activeOrgId ?? undefined;

export const PropertyUsersRepository = {
    async listByProperty(propertyId: uuid): Promise<PropertyUserRecord[]> {
        await requirePermission("property_users.view");
        const { items } = await api.people.list(propertyId, { actingOrganization: actingOrganization() });
        return items.map(toPropertyUser);
    },

    async listAssignableUsers(): Promise<AssignableUser[]> {
        await requirePermission("property_users.view");
        const customer = actingOrganization();
        if (!customer) return [];
        const { items } = await api.customers.team(customer);
        return items.map(({ id, name, email }) => ({ id, name, email }));
    },

    async addUser(propertyId: uuid, userId: uuid, metaData: PropertyUserMetadata = {}, isPrimaryContact: boolean = false): Promise<void> {
        await requirePermission("property_users.assign");
        await api.people.assign(propertyId, { person: userId, ...toAssignmentDetails(metaData, isPrimaryContact) });
    },

    async addContact(propertyId: uuid, email: string, metaData: PropertyUserMetadata = {}, isPrimaryContact: boolean = false): Promise<void> {
        await requirePermission("property_users.assign");
        await api.people.addContact(propertyId, { email, ...toAssignmentDetails(metaData, isPrimaryContact) });
    },

    async removeUser(propertyId: uuid, id: uuid): Promise<void> {
        await requirePermission("property_users.remove");
        await api.people.remove(propertyId, id);
    },

    async updateMeta(propertyId: uuid, id: uuid, metaData: PropertyUserMetadata, isPrimaryContact: boolean): Promise<void> {
        await requirePermission("property_users.assign");
        await api.people.setDetails(propertyId, id, toAssignmentDetails(metaData, isPrimaryContact));
    },
};
